import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
    });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    // Initialize EmailJS with public key
    useEffect(() => {
        emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '');
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');

        try {
            // Send email using EmailJS
            const result = await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
                {
                    from_name: formData.name,
                    from_email: formData.email,
                    company: formData.company,
                    phone: formData.phone,
                    message: formData.message,
                }
            );

            console.log('EmailJS Success:', result);
            setStatus('success');
            setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        } catch (error) {
            console.error('EmailJS Error:', error);
            setStatus('error');
        }
    };

    return (
        <section id="contact" className="py-24 bg-navy-900 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-navy-800/50 to-navy-900 pointer-events-none" />

            <div className="max-w-4xl mx-auto px-6 relative z-10">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Ready to Automate Your Business?
                    </h2>
                    <p className="text-text-muted">
                        Book a free consultation to see how we can help you save time and money.
                    </p>
                </div>

                <div className="bg-navy-800 border border-white/[0.05] rounded-2xl p-8 md:p-12 shadow-2xl">
                    {status === 'success' ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="w-8 h-8 text-green-500" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Message Received!</h3>
                            <p className="text-text-muted">
                                Thanks for reaching out. We'll get back to you within 24 hours.
                            </p>
                            <button
                                onClick={() => setStatus('idle')}
                                className="mt-8 text-brand-cyan font-semibold hover:text-white transition-colors"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : status === 'error' ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Oops! Something went wrong</h3>
                            <p className="text-text-muted mb-4">
                                We couldn't send your message. Please try again or contact us directly at owechada@gmail.com
                            </p>
                            <button
                                onClick={() => setStatus('idle')}
                                className="mt-4 text-brand-cyan font-semibold hover:text-white transition-colors"
                            >
                                Try again
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label htmlFor="name" className="text-sm font-medium text-text-muted">Full Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-white/[0.1] text-white focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all"
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="email" className="text-sm font-medium text-text-muted">Work Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-white/[0.1] text-white focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all"
                                        placeholder="john@company.com"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label htmlFor="company" className="text-sm font-medium text-text-muted">Company Name</label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        required
                                        value={formData.company}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-white/[0.1] text-white focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all"
                                        placeholder="Acme Inc."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="phone" className="text-sm font-medium text-text-muted">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-white/[0.1] text-white focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all"
                                        placeholder="+234..."
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="message" className="text-sm font-medium text-text-muted">How can we help?</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-white/[0.1] text-white focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all resize-none"
                                    placeholder="Tell us about your manual processes..."
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="w-full py-4 rounded-xl bg-gradient-to-r from-brand-orange to-brand-coral text-white font-bold shadow-lg shadow-brand-orange/20 hover:shadow-brand-orange/40 transition-all hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {status === 'submitting' ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Book Consultation
                                        <Send className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Contact;
